import { useEffect, useState } from 'react';
import {
    Paper,
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { jwtDecode } from "jwt-decode";
import axios from "axios";

const SecInfoHistorico = ({ produtoRetorno }) => {
    const [modalAberto, setModalAberto] = useState(null);
    const [historicoData, setHistoricoData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const baseURL = process.env.REACT_APP_API_BASE_URL;

    // Mapear produto para idProduto
    const getIdProduto = (produtoRetorno) => {
        if (produtoRetorno === "Agente de Conteúdo") return 3;
        if (produtoRetorno === "Agente de Pré-Venda") return 2;
        if (produtoRetorno === "Agente de Pós-Venda") return 5;
        if (produtoRetorno === "Agente de Vendas" || produtoRetorno === "Agente do Corretor") return 1;
        return null;
    };

    const getNomeCampoCorreto = (nome) => {
        switch (nome) {
            case "Contexto_produto":
            case "Contextoproduto":
            case "contextoProduto":
                return "Informações do Produto";

            case "Contexto_institucional":
            case "Contextoinstitucional":
                return "Informações Institucionais";

            case "Contexto1":
                return "Informações Adicionais 1";

            case "Contexto2":
                return "Informações Adicionais 2";

            default:
                return nome;
        }
    };

    const formatarDataHora = (valor) => {
        if (!valor) return '';
        if (typeof valor !== 'string') return String(valor);

        // Se ja estiver no formato desejado, apenas retorna.
        if (/^\d{2}\/\d{2}\/\d{4}\s*-\s*\d{2}:\d{2}$/.test(valor)) {
            return valor;
        }

        // Extrai data/hora diretamente da string para evitar ajuste de fuso.
        const match = valor.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))/);
        if (match) {
            const [, yyyy, mm, dd, hh, min] = match;
            return `${dd}/${mm}/${yyyy} - ${hh}:${min}`;
        }

        return valor;
    };


    useEffect(() => {
        const fetchHistorico = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('jwtToken');
                if (!token) {
                    console.error("Token não encontrado no localStorage.");
                    return;
                }

                const decodedToken = jwtDecode(token);
                const idClienteAipim = decodedToken.id_cliente;
                const idProduto = getIdProduto(produtoRetorno);

                if (!idProduto) {
                    console.error("Produto não identificado");
                    return;
                }

                const params = {
                    idClienteAipim: idClienteAipim,
                    idProduto: idProduto
                };

                const response = await axios.get(`${baseURL}/produto/Justificativa/historico`, {
                    params,
                    headers: { Authorization: `Bearer ${token}` }
                });

                // 1) Ordena os registros pela data mais recente (do backend)
                const dadosOrdenados = [...response.data].sort((a, b) => {
                    return new Date(b.dataHoraCadastro) - new Date(a.dataHoraCadastro);
                });

                // 2) Transforma para o formato usado na tela
                const dadosTransformados = dadosOrdenados.map((item, index) => {
                    return {
                        id: index + 1,
                        nome: getNomeCampoCorreto(item.campo),
                        alteradoPor: `${item.nomeUsuario}`,
                        data: formatarDataHora(item.dataHoraCadastro),
                        justificativa: item.justificativa,
                        mudancas: {
                            antes: item.valorAnterior,
                            depois: item.valorNovo
                        }
                    };
                });

                setHistoricoData(dadosTransformados);

            } catch (error) {
                console.error("Erro ao buscar histórico:", error);
                if (error.response?.status === 401 ||
                    error.response?.data?.message?.toLowerCase().includes("token")) {
                    console.error("Token expirado ou inválido");
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchHistorico();
    }, [produtoRetorno, baseURL]);

    const handleAbrirModal = (id) => {
        setModalAberto(id);
    };

    const handleFecharModal = () => {
        setModalAberto(null);
    };

    const itemSelecionado = historicoData.find(item => item.id === modalAberto);

    return (
        <>
            <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Informação
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Quem alterou
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Data da Alteração
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Justificativa
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Alterações
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Box sx={{ py: 5 }}>
                                        <CircularProgress size={40} />
                                        <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                                            Carregando histórico...
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : historicoData.length > 0 ? (
                            historicoData.map((item) => (
                                <TableRow
                                    key={item.id}
                                    sx={{
                                        '&:hover': { backgroundColor: 'action.hover' },
                                        '&:last-child td, &:last-child th': { border: 0 }
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {item.nome}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {item.alteradoPor}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {item.data}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                            {item.justificativa}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            onClick={() => handleAbrirModal(item.id)}
                                            size="small"
                                            sx={{
                                                color: 'primary.main',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                }
                                            }}
                                        >
                                            <VisibilityIcon sx={{ fontSize: '20px' }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography sx={{ py: 3, color: 'text.secondary' }}>
                                        Nenhum histórico disponível
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Modal de Detalhes das Mudanças */}
            <Dialog
                open={modalAberto !== null}
                onClose={handleFecharModal}
                maxWidth="md"
                fullWidth
            >
                {itemSelecionado && (
                    <>
                        <DialogTitle sx={{
                            backgroundColor: 'background.paper',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Detalhes da Alteração
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {itemSelecionado.nome} • {itemSelecionado.data}
                                </Typography>
                            </Box>
                            <IconButton onClick={handleFecharModal} size="small">
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ p: 3, mt: 2 }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: 2,
                                backgroundColor: 'background.default',
                                p: 2.5,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}>
                                {/* ENVOLVER "Antes" em um Box */}
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#d32f2f' }}>
                                        Antes:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            p: 2,
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.12)' : '#ffebee',
                                            color: 'text.primary',
                                            borderRadius: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '0.9rem',
                                            lineHeight: 1.6,
                                            maxHeight: '400px',
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            overflowWrap: 'anywhere',
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {itemSelecionado.mudancas.antes}
                                    </Typography>
                                </Box>

                                {/* ENVOLVER "Depois" em um Box */}
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#2e7d32' }}>
                                        Depois:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            p: 2,
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.12)' : '#e8f5e9',
                                            color: 'text.primary',
                                            borderRadius: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '0.9rem',
                                            lineHeight: 1.6,
                                            maxHeight: '400px',
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            overflowWrap: 'anywhere',
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {itemSelecionado.mudancas.depois}
                                    </Typography>
                                </Box>
                            </Box>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default SecInfoHistorico;
